import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import WorkoutProgressTable from "../components/WorkoutProgressTable";
import ExerciseProgressTable from "../components/ExerciseProgressTable";
import EmptyStateMessage from "../components/EmptyStateMessage";
import SeoHead from "../components/SeoHead";
import "./Home.css";
import "../styles/Common.css";
import { useHomeData } from "../hooks/useHomeData";
import { formatWeightRange } from "../utils/formatWeightRange";
import { todayYMDLocal } from "../utils/dateUtils";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { getAllExercisesCatalog } from "../services/customExerciseService";
import { buildLocalizedAlternates, getPrerenderPublicPaths } from "../seo/routePolicy";
import { muscleGroups } from "../data/muscleGroups";
import { useSnapshotRenderMode } from "../utils/prerender";
import { buildProgressDates } from "./homeDateWindow";

function Home() {
    const { t, localizePath } = useI18n();
    const { dates, loggedExercises, exerciseLog } = useHomeData();
    const snapshotMode = useSnapshotRenderMode();
    const [displayToday, setDisplayToday] = useState(() => {
        if (typeof document !== "undefined") {
            const saved = document.getElementById("home-snapshot-today")?.getAttribute("data-today");
            if (saved) {
                return saved;
            }
        }

        return todayYMDLocal();
    });
    const [progressView, setProgressView] = useState("muscleGroups");
    const [showIndirect, setShowIndirect] = useState(true);

    useEffect(() => {
        setDisplayToday(todayYMDLocal());
    }, []);

    useEffect(() => {
        try {
            const saved = localStorage.getItem("home_showIndirect");
            if (saved !== null) {
                setShowIndirect(saved === "true");
            }
        } catch (_) {
            // ignore storage errors
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem("home_showIndirect", String(showIndirect));
        } catch (_) {
            // ignore storage errors
        }
    }, [showIndirect]);

    const exerciseMap = useMemo(
        () => Object.fromEntries(getAllExercisesCatalog().map((exercise) => [exercise.id, exercise])),
        []
    );

    const hasAnyLoggedExercises = useMemo(() => {
        return Object.values(exerciseLog || {}).some((day) => day && Object.keys(day).length > 0);
    }, [exerciseLog]);
    const visibleDates = useMemo(() => {
        return buildProgressDates({
            dates,
            displayToday,
            exerciseLog,
            minimumDates: 5,
            maxDates: 8,
        });
    }, [dates, displayToday, exerciseLog]);
    const homeJsonLd = useMemo(
        () => {
            const siteUrl = "https://fit-log.com";
            const dashboardUrl = `${siteUrl}${localizePath("/")}`;

            const breadcrumbList = {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": t("breadcrumbs.dashboard"),
                        "item": dashboardUrl
                    }
                ]
            };

            const appSchema = {
                "@type": "SoftwareApplication",
                "name": "Fit-Log",
                "applicationCategory": "HealthApplication",
                "operatingSystem": "Web",
                "description": t("home.jsonLdDescription"),
                "url": dashboardUrl,
            };

            return {
                "@context": "https://schema.org",
                "@graph": [breadcrumbList, appSchema]
            };
        },
        [t, localizePath]
    );
    const prerenderPaths = useMemo(
        () => getPrerenderPublicPaths(muscleGroups.map((group) => group.id)),
        []
    );

    return (
        <div className="homePage pageContainer">
            <SeoHead
                title={`Fit-Log - ${t("home.title")}`}
                description={t("home.jsonLdDescription")}
                canonicalPath={localizePath("/")}
                jsonLd={homeJsonLd}
                alternateLocales={buildLocalizedAlternates("/")}
            />
            <Breadcrumbs current={t("breadcrumbs.dashboard")} />
            <header className="homeHero">
                <h1 className="sectionHeading">{t("home.title")}</h1>
                <p className="homeSubtitle">{t("home.subtitle")}</p>
            </header>
            <span id="home-snapshot-today" data-today={displayToday} hidden aria-hidden="true" />
            {snapshotMode && (
                <nav aria-hidden="true" style={{ display: "none" }}>
                    {prerenderPaths.map((path) => (
                        <a key={path} href={path}>{path}</a>
                    ))}
                </nav>
            )}

            <div className="section">
                <div className="sectionHeadingRow">
                    <h2 className="sectionHeading">{t("home.loggedToday")}</h2>
                    {loggedExercises.length > 0 && (
                        <Link
                            to={localizePath("/SelectExercise")}
                            className="appButton homeActionButton"
                        >
                            <span>{t("home.logExercise")}</span>
                            <span className="homeActionButtonSymbol" aria-hidden="true">+</span>
                        </Link>
                    )}
                </div>
                {loggedExercises.length === 0 ? (
                    <EmptyStateMessage />
                ) : (
                    <table className="compactExerciseTable">
                        <thead>
                        <tr>
                            <th>{t("home.exercise")}</th>
                            <th>{t("home.setsCount")}</th>
                            <th>{t("home.weight")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loggedExercises.map(({ exerciseId, sets }) => {
                            const exercise = exerciseMap[exerciseId];
                            const exerciseName = exercise
                                ? getLocalizedExerciseName(exercise, t)
                                : t("home.unknownExercise", { id: exerciseId });

                            return (
                                <tr key={exerciseId}>
                                    <td>
                                        {snapshotMode ? (
                                            <span className="linkButton appLinkButton">{exerciseName}</span>
                                        ) : (
                                            <Link
                                                to={localizePath(`/LogExercise/${exerciseId}/${displayToday}`)}
                                                className="linkButton appLinkButton"
                                            >
                                                {exerciseName}
                                            </Link>
                                        )}
                                    </td>
                                    <td>{sets.length}</td>
                                    <td>{formatWeightRange(sets, { naLabel: t("common.na") })}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="section">
                <div className="sectionHeadingRow">
                    <h2 className="sectionHeading">{t("home.progressTitle")}</h2>
                </div>
                <div className="progressControls">
                    <div
                        className="progressTabs"
                        role="tablist"
                        aria-label={t("home.progressTabsLabel")}
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressView === "muscleGroups"}
                            className={`progressTab${progressView === "muscleGroups" ? " progressTab--active" : ""}`}
                            onClick={() => setProgressView("muscleGroups")}
                        >
                            {t("home.progressByMuscleGroups")}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressView === "exercisesByDate"}
                            className={`progressTab${progressView === "exercisesByDate" ? " progressTab--active" : ""}`}
                            onClick={() => setProgressView("exercisesByDate")}
                        >
                            {t("home.progressByDate")}
                        </button>
                    </div>
                    {hasAnyLoggedExercises && progressView === "muscleGroups" && (
                        <button
                            type="button"
                            className="indirectToggleButton"
                            onClick={() => setShowIndirect((prev) => !prev)}
                            aria-pressed={showIndirect}
                            aria-label={t("home.toggleIndirectAria")}
                            title={t("home.toggleIndirectAria")}
                        >
                            <input
                                type="checkbox"
                                checked={showIndirect}
                                readOnly
                                aria-hidden="true"
                            />
                            <span>{t("home.toggleIndirect")}</span>
                        </button>
                    )}
                </div>
                <div className="tableContainer">
                    {progressView === "muscleGroups" ? (
                        <WorkoutProgressTable
                            dates={visibleDates}
                            exerciseLog={exerciseLog}
                            showIndirect={showIndirect}
                        />
                    ) : (
                        <ExerciseProgressTable
                            dates={visibleDates}
                            exerciseLog={exerciseLog}
                        />
                    )}
                </div>
            </div>

            { /* Export/Import buttons removed as requested */ }
        </div>
    );
}

export default Home;

